import React from 'react';
import { IProps } from './types';
import * as Yup from 'yup';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import commonService from 'utils/commonService';
import service from 'bff/services/fireProdService';
import sweetAlertService from 'utils/sweetAlertService';
import { SweetAlertIcon } from 'constants/enums/sweetAlertIcon.enum';
import { InsureProcessPaymentFormValue, InsureProcessPaymentState } from 'constants/models/components/insureProcessFire/insureProcessPayment';
import { PaymentMethod } from 'constants/enums/paymentMethod.enum';
import { InsuredRoleType } from 'constants/enums/insuredRoleType.enum';
import { Code, SysOption } from 'bff/models/info';
import _ from 'lodash';
import { BankContent, PaymentContent } from 'bff/models/product/paymentMethod';
import { ApplyType } from 'constants/enums/applyType.enum';
import InputWithRegex from 'components/InputWithRegex';
// import moment from 'moment';
import moment from 'moment-timezone';
import { BaseFireInsuredContent, BaseFirePolicyHolderContent } from 'constants/models/components/insureProcessFire/insureProcessFill';
import { PaymentEBillReq, PaymentNccReq } from 'bff/models/product';
import { InsuredRelation } from 'constants/enums/insuredRelation.enum';
import { NationalType } from 'constants/enums/nationalType.enum';
import { CodeId } from 'constants/enums/codeId.enum';
import { PayerContent } from 'bff/models/member/boPayby';
import { RuleCheckInsuredType } from 'constants/enums/ruleCheckInsuredType.enum';
import { InprocessSelectState } from 'constants/models/components/insureProcessFire/insureProcessSelect';

const initFormVal: InsureProcessPaymentFormValue = {
  /** 支付方式 */
  PAYMENT_METHOD: PaymentMethod.CreditCard,
  /** 是否同意支付條款 */
  PAYMENT_CLAIM: false,
  /** 付款人身分 - 信用卡 */
  CREDIT_PAYER_TYPE: InsuredRoleType.Application,
  /** 付款人 ID - 信用卡 */
  CREDIT_PAYER_ID: '',
  /** 卡號 1 */
  PAN1st: '',
  /** 卡號 2 */
  PAN2nd: '',
  /** 卡號 3 */
  PAN3rd: '',
  /** 卡號 4 */
  PAN4th: '',
  /** 信用卡檢查碼 */
  CARD_VERIFY_CODE: '',
  /** 卡片到期日-月份 */
  EXPIREDATE_MONTH: '',
  /** 卡片到期日-年分 */
  EXPIREDATE_YEAR: '',
  /** 付款人身分 - 銀行 */
  BANK_PAYER_TYPE: InsuredRoleType.Application,
  /** 付款人 ID - 銀行 */
  BANK_PAYER_ID: '',
  /** 銀行代碼 */
  BANK_CODE: '',
  /** 銀行帳號 */
  BANK_ACCOUNT: ''
};

let formInitialValues = initFormVal;

// const formInitialValues = {
//   PAYMENT_METHOD: 'credit-card',
//   RECEIVE_INFO: ''
// };

const validationSchema = Yup.object().shape({
  PAYMENT_METHOD: Yup.string().required('此為必填欄位'),
  CREDIT_PAYER_TYPE: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.CreditCard ? schema.required('此為必填欄位。') : schema;
  }),
  CREDIT_PAYER_ID: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.CreditCard ? schema.required('此為必填欄位。') : schema;
  }),
  PAN1st: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.CreditCard ? schema.required('此為必填欄位。').length(4, '長度不可超過（少於）4 字元') : schema;
  }),
  PAN2nd: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.CreditCard ? schema.required('此為必填欄位。').length(4, '長度不可超過（少於）4 字元') : schema;
  }),
  PAN3rd: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.CreditCard ? schema.required('此為必填欄位。').length(4, '長度不可超過（少於）4 字元') : schema;
  }),
  PAN4th: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.CreditCard ? schema.required('此為必填欄位。').length(4, '長度不可超過（少於）4 字元') : schema;
  }),
  EXPIREDATE_MONTH: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.CreditCard ? schema.required('此為必填欄位。').length(2, '長度不可超過（少於）2 字元') : schema;
  }),
  EXPIREDATE_YEAR: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.CreditCard ? schema.required('此為必填欄位。').length(2, '長度不可超過（少於）2 字元') : schema;
  }),
  CARD_VERIFY_CODE: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.CreditCard ? schema.required('此為必填欄位。').length(3, '長度不可超過（少於）3 字元') : schema;
  }),
  BANK_PAYER_ID: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.BankAccount ? schema.required('此為必填欄位。') : schema;
  }),
  BANK_PAYER_TYPE: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.BankAccount ? schema.required('此為必填欄位。') : schema;
  }),
  BANK_CODE: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.BankAccount ? schema.required('此為必填欄位。') : schema;
  }),
  BANK_ACCOUNT: Yup.string().when('PAYMENT_METHOD', (method: any, schema: any) => {
    return method === PaymentMethod.BankAccount ? schema.required('此為必填欄位。').min(11, '長度不可少於 11 字元').max(14, '長度不可超過 14 字元') : schema;
  })
});

class InsureProcessPayment extends React.Component<IProps, InsureProcessPaymentState> {
  private formikRef: any = React.createRef();

  state: InsureProcessPaymentState = {
    activePaymentMethod: PaymentMethod.CreditCard,
    agreeContract: false,
    activeInsRoleType: InsuredRoleType.Application,
    paymentMethod: {
      PAYMENT_INFO: {
        COMMON_MSG: {
          BANKACCOUNT: {
            BANK: new Array(0),
            NOTICE_B: '',
            NOTICE_C: ''
          },
          CLAIMS: {
            HEAD: '',
            BODY: '',
            ANSWER: {
              HEAD: '',
              BODY: ''
            }
          },
          CREDITCARD: {
            BANK: new Array(0),
            NOTICE_B: '',
            NOTICE_C: ''
          }
        },
        LEGAL_RELATION: new Array(0),
        PAYMENT_OPTION: [{}]
      }
    },
    insRoleTypeSysOpts: new Array(0),
    barcodeNoteCodes: new Array(0),
    barcodeNoticeCodes: new Array(0),
    isSelf: false,
    onlyApplicant: false,
    onlyInsured: false,
    showCreditCardMethod: true
  }

  componentDidMount = async () => {
    commonService.scrollToTop();

    let isSelf: any;
    let serialNo: any;
    let activePaymentMethod = PaymentMethod.CreditCard;
    let activeInsRoleType = InsuredRoleType.Application;
    // 篩選去除法人項目-要保人
    let noLegalPolicyholders: any;
    // 篩選去除法人項目-被保人
    let noLegalInsureds: any;

    if (this.props.boPayby.APPLY_NO) {
      serialNo = this.props.boPayby.APPLY_NO;

      const policyholders =
        _.filter(this.props.boPayby.PAYER_LIST, (payer: PayerContent) => payer.LTYPE === RuleCheckInsuredType.Applicant);

      const insureds =
        _.filter(this.props.boPayby.PAYER_LIST, (payer: PayerContent) => payer.LTYPE === RuleCheckInsuredType.Insured);

      isSelf =
        policyholders.length === 1 &&
        insureds.length === 1 &&
        // policyholders[0].ID_TYPE !== NationalType.Legal &&
        // insureds[0].ID_TYPE !== NationalType.Legal &&
        policyholders[0].ID === insureds[0].ID;

      if (isSelf) {
        formInitialValues = {
          ...formInitialValues,
          BANK_PAYER_TYPE: InsuredRoleType.Application,
          BANK_PAYER_ID: policyholders[0].ID,
          CREDIT_PAYER_TYPE: InsuredRoleType.Application,
          CREDIT_PAYER_ID: policyholders[0].ID
        };
      }

      // 篩選去除法人項目
      noLegalPolicyholders = _.filter(policyholders,
        (payer: PayerContent) => payer.ID_TYPE !== NationalType.Legal);

      // 篩選去除法人項目
      noLegalInsureds = _.filter(insureds,
        (payer: PayerContent) => payer.ID_TYPE !== NationalType.Legal);

      if (!isSelf && noLegalPolicyholders.length === 1) {
        formInitialValues = {
          ...formInitialValues,
          CREDIT_PAYER_TYPE: InsuredRoleType.Application,
          CREDIT_PAYER_ID: noLegalPolicyholders[0].ID || ''
        };
      }

      if (!isSelf && noLegalPolicyholders.length === 0 && noLegalInsureds.length === 1) {
        activeInsRoleType = InsuredRoleType.Insured;

        formInitialValues = {
          ...formInitialValues,
          CREDIT_PAYER_TYPE: InsuredRoleType.Insured,
          CREDIT_PAYER_ID: noLegalInsureds[0].ID || ''
        };
      }

      // if (!isSelf && noLegalPolicyholders.length === 0 && noLegalInsureds.length === 0) {
      //   activePaymentMethod = PaymentMethod.BankAccount;
      //   formInitialValues = {
      //     ...formInitialValues,
      //     PAYMENT_METHOD: PaymentMethod.BankAccount
      //   };
      // }

      if (noLegalPolicyholders.length === 0 && noLegalInsureds.length === 0) {
        activePaymentMethod = PaymentMethod.BankAccount;
        formInitialValues = {
          ...formInitialValues,
          PAYMENT_METHOD: PaymentMethod.BankAccount
        };
      }
    } else {
      const { tempFillState, tempFillFormValue } = this.props.tempFill;
      if (!tempFillState || !tempFillFormValue) return;

      serialNo = tempFillState.serialNo as string;

      isSelf = tempFillFormValue.POLICYHOLDERS.length === 1 &&
        tempFillFormValue.INSUREDS.length === 1 &&
        tempFillFormValue.INSUREDS[0].INSURED_RELATION === InsuredRelation.Self;

      if (isSelf) {
        formInitialValues = {
          ...formInitialValues,
          BANK_PAYER_TYPE: InsuredRoleType.Application,
          BANK_PAYER_ID: tempFillFormValue.POLICYHOLDERS[0].POLICYHOLDER_ID || '',
          CREDIT_PAYER_TYPE: InsuredRoleType.Application,
          CREDIT_PAYER_ID: tempFillFormValue.POLICYHOLDERS[0].POLICYHOLDER_ID || ''
        };
      }

      // 篩選去除法人項目
      noLegalPolicyholders = _.filter(tempFillFormValue.POLICYHOLDERS,
        (holder: BaseFirePolicyHolderContent) => holder.POLICYHOLDER_NATIONALITY_TYPE !== NationalType.Legal);

      // 篩選去除法人項目
      noLegalInsureds = _.filter(tempFillFormValue.INSUREDS,
        (insured: BaseFireInsuredContent) => insured.INSURED_NATIONALITY_TYPE !== NationalType.Legal);

      if (!isSelf && noLegalPolicyholders.length === 1) {
        formInitialValues = {
          ...formInitialValues,
          CREDIT_PAYER_TYPE: InsuredRoleType.Application,
          CREDIT_PAYER_ID: noLegalPolicyholders[0].POLICYHOLDER_ID || ''
        };
      }

      if (!isSelf && noLegalPolicyholders.length === 0 && noLegalInsureds.length === 1) {
        activeInsRoleType = InsuredRoleType.Insured;

        formInitialValues = {
          ...formInitialValues,
          CREDIT_PAYER_TYPE: InsuredRoleType.Insured,
          CREDIT_PAYER_ID: noLegalInsureds[0].INSURED_ID || ''
        };
      }

      // if (!isSelf && noLegalPolicyholders.length === 0 && noLegalInsureds.length === 0) {
      //   activePaymentMethod = PaymentMethod.BankAccount;
      //   formInitialValues = {
      //     ...formInitialValues,
      //     PAYMENT_METHOD: PaymentMethod.BankAccount
      //   };
      // }

      if (noLegalPolicyholders.length === 0 && noLegalInsureds.length === 0) {
        activePaymentMethod = PaymentMethod.BankAccount;
        formInitialValues = {
          ...formInitialValues,
          PAYMENT_METHOD: PaymentMethod.BankAccount
        };
      }
      // console.log('temp State', JSON.stringify(this.props.tempState));
    }

    const initSetting = await service.fetchInitPayment(serialNo, this.props.insTypeId);
    // console.log('initSetting', initSetting);
    if (Object.keys(initSetting.paymentMethod).length === 0) {
      sweetAlertService.modal({
        icon: SweetAlertIcon.Info,
        html: '無法取得付款方式'
      });

      return;
    }

    this.setState({
      activeInsRoleType: activeInsRoleType,
      activePaymentMethod: activePaymentMethod,
      paymentMethod: { ...initSetting.paymentMethod },
      insRoleTypeSysOpts: [...initSetting.insRoleTypeSysOpts],
      barcodeNoteCodes: [...initSetting.barcodeNoteCodes],
      barcodeNoticeCodes: [...initSetting.barcodeNoticeCodes],
      isSelf: isSelf,
      onlyApplicant: noLegalPolicyholders.length > 0 && noLegalInsureds.length === 0,
      onlyInsured: noLegalPolicyholders.length === 0 && noLegalInsureds.length > 0,
      showCreditCardMethod: noLegalPolicyholders.length > 0 || noLegalInsureds.length > 0
    });

    // console.log('temp State', JSON.stringify(this.props.tempState));
  }

  componentWillUnmount () {
    if (!this.formikRef || !this.formikRef.current) return;

    const values = this.formikRef.current.values as InsureProcessPaymentFormValue;

    this.props.onUnmount(values, this.state);

    formInitialValues = {
      ...initFormVal
    };
  }

  /**
   * @description 表單 Submit 事件
  */
  handleFormSubmit = async (values: InsureProcessPaymentFormValue) => {
    const btnPay = document.getElementById('btn_pay') as HTMLButtonElement;
    if (!btnPay) return;

    btnPay.disabled = true;

    if (values.PAYMENT_METHOD !== PaymentMethod.BarCode) {
      let fee;
      let applyNo;
      let applyType;
      if (this.props.boPayby.APPLY_NO) {
        fee = this.props.boPayby.POLICY_FEE;
        applyNo = this.props.boPayby.APPLY_NO;
        applyType = this.props.boPayby.BC_TYPE;
      } else {
        const { tempSelectState } = this.props.tempSelect;
        const { tempFillState } = this.props.tempFill;

        fee = tempSelectState && tempSelectState.evalFireRes.TOTAL_FEE
          ? tempSelectState.evalFireRes.TOTAL_FEE
          : '';
        applyNo = tempFillState?.serialNo as string;
        applyType = tempFillState?.applyType as string;
      }

      // 執行線上付款-信用卡
      if (this.state.activePaymentMethod === PaymentMethod.CreditCard) {
        const paymentNccReq: PaymentNccReq = {
          APPLY_NO: applyNo,
          PAN: `${values.PAN1st}${values.PAN2nd}${values.PAN3rd}${values.PAN4th}`,
          EXPIREDATE: `${values.EXPIREDATE_MONTH}${values.EXPIREDATE_YEAR}`,
          TRANSAMT: fee ? `${fee}` : '0',
          IDNUMBER: values.CREDIT_PAYER_ID,
          PAYER_NAME: this.handleGetPayerName(values.CREDIT_PAYER_TYPE, values.CREDIT_PAYER_ID),
          BC_TYPE: applyType
        };
        // console.log('paymentNccReq', JSON.stringify(paymentNccReq));
        const res = await service.paymentNccc(paymentNccReq);
        if (!res) {
          btnPay.disabled = false;
          return;
        }
      }

      // 執行線上付款-活期帳戶
      if (this.state.activePaymentMethod === PaymentMethod.BankAccount) {
        const paymentEBillReq: PaymentEBillReq = {
          personId: values.BANK_PAYER_ID,
          trnsOutBank: values.BANK_CODE,
          trnsOutAcct: values.BANK_ACCOUNT,
          // 總比數固定帶 1
          totalCount: '1',
          totalAmount: fee ? `${fee}` : '0',
          APPLY_NO: applyNo,
          PAYER_NAME: this.handleGetPayerName(values.BANK_PAYER_TYPE, values.BANK_PAYER_ID)
        };
        // console.log('paymentEBillReq', JSON.stringify(paymentEBillReq));
        const res = await service.paymentEBill(paymentEBillReq);
        if (!res) {
          btnPay.disabled = false;
          return;
        }
      }
    }

    this.props.onSubmit(values, this.state);
    btnPay.disabled = false;
  }

  /**
   * @description 取得付款人姓名
  */
  handleGetPayerName = (payerType: string, id: string) => {
    if (this.props.boPayby.APPLY_NO) {
      return _.find(this.props.boPayby.PAYER_LIST, (payer: PayerContent) => payer.ID === id)?.NAME || '';
    }

    const { tempFillFormValue } = this.props.tempFill;
    if (!tempFillFormValue) return '';

    if (payerType === InsuredRoleType.Application) {
      return _.find(tempFillFormValue.POLICYHOLDERS, (holder: BaseFirePolicyHolderContent) => holder.POLICYHOLDER_ID === id)?.POLICYHOLDER_NAME || '';
    }

    return _.find(tempFillFormValue.INSUREDS, (insured: BaseFireInsuredContent) => insured.INSURED_ID === id)?.INSURED_NAME || '';
  }

  /**
   * @description 付款方式切換方法
   */
  handlePaymentMethodClick = (val: string) => {
    if (this.state.activePaymentMethod === val || !this.formikRef || !this.formikRef.current) return;
    let bankPayerId = '';
    let creditPayerType = InsuredRoleType.Application;
    let creditPayerId = '';
    let activeInsRoleType = InsuredRoleType.Application;

    if (this.props.boPayby.APPLY_NO) {
      const policyholders =
        _.filter(this.props.boPayby.PAYER_LIST, (payer: PayerContent) => payer.LTYPE === RuleCheckInsuredType.Applicant);
      const insureds =
        _.filter(this.props.boPayby.PAYER_LIST, (payer: PayerContent) => payer.LTYPE === RuleCheckInsuredType.Insured);

      // 篩選去除法人項目
      const noLegalPolicyholders = _.filter(policyholders,
        (payer: PayerContent) => payer.ID_TYPE !== NationalType.Legal);

      // 篩選去除法人項目
      const noLegalInsureds = _.filter(insureds,
        (payer: PayerContent) => payer.ID_TYPE !== NationalType.Legal);

      if (this.state.isSelf) {
        bankPayerId = policyholders[0].ID;
        creditPayerId = policyholders[0].ID;
      }

      if (val === PaymentMethod.CreditCard && this.state.onlyApplicant) {
        if (noLegalPolicyholders.length === 1) {
          creditPayerId = noLegalPolicyholders[0].ID;
          bankPayerId = noLegalPolicyholders[0].ID;
        }
      }

      if (val === PaymentMethod.CreditCard && this.state.onlyInsured) {
        activeInsRoleType = InsuredRoleType.Insured;
        creditPayerType = InsuredRoleType.Insured;
        if (noLegalInsureds.length === 1) {
          creditPayerId = noLegalInsureds[0].ID;
          bankPayerId = noLegalInsureds[0].ID;
        }
      }
    } else {
      const { tempFillFormValue } = this.props.tempFill;
      if (!tempFillFormValue) return;

      // 篩選去除法人項目
      const noLegalPolicyholders = _.filter(tempFillFormValue.POLICYHOLDERS,
        (holder: BaseFirePolicyHolderContent) => holder.POLICYHOLDER_NATIONALITY_TYPE !== NationalType.Legal);

      // 篩選去除法人項目
      const noLegalInsureds = _.filter(tempFillFormValue.INSUREDS,
        (insured: BaseFireInsuredContent) => insured.INSURED_NATIONALITY_TYPE !== NationalType.Legal);

      if (this.state.isSelf) {
        bankPayerId = tempFillFormValue.POLICYHOLDERS[0].POLICYHOLDER_ID;
        creditPayerId = tempFillFormValue.POLICYHOLDERS[0].POLICYHOLDER_ID;
      }

      if (val === PaymentMethod.CreditCard && this.state.onlyApplicant) {
        if (noLegalPolicyholders.length === 1) {
          creditPayerId = noLegalPolicyholders[0].POLICYHOLDER_ID;
          bankPayerId = noLegalPolicyholders[0].POLICYHOLDER_ID;
        }
      }

      if (val === PaymentMethod.CreditCard && this.state.onlyInsured) {
        activeInsRoleType = InsuredRoleType.Insured;
        creditPayerType = InsuredRoleType.Insured;
        if (noLegalInsureds.length === 1) {
          creditPayerId = noLegalInsureds[0].INSURED_ID;
          bankPayerId = noLegalInsureds[0].INSURED_ID;
        }
      }
    }

    if (val === PaymentMethod.BarCode) {
      sweetAlertService.modal({
        icon: SweetAlertIcon.Info,
        title: '行動條碼繳費說明',
        html: commonService.getCodeMsg(this.props.UICodes, CodeId.U040)
      });
    }

    const setFieldValue = this.formikRef.current.setFieldValue;
    setFieldValue('CREDIT_PAYER_TYPE', creditPayerType);
    setFieldValue('CREDIT_PAYER_ID', creditPayerId);
    setFieldValue('BANK_PAYER_TYPE', InsuredRoleType.Application);
    setFieldValue('BANK_PAYER_ID', bankPayerId);

    this.setState({ activePaymentMethod: val, activeInsRoleType: activeInsRoleType });
  }

  /**
   * @description 取得投保金額
  */
  handleGetFee = (tempSelectState?: InprocessSelectState) => {
    if (tempSelectState && tempSelectState.evalFireRes && tempSelectState.evalFireRes.TOTAL_FEE) {
      return `NT$ ${commonService.thousandFormat(`${tempSelectState.evalFireRes.TOTAL_FEE}`)} 元`;
    }

    if (this.props.boPayby.APPLY_NO) {
      return `NT$ ${commonService.thousandFormat(this.props.boPayby.POLICY_FEE ? `${this.props.boPayby.POLICY_FEE}` : '')}`;
    }
  }

  render () {
    const { tempFillFormValue, tempFillState } = this.props.tempFill;
    const { tempSelectState } = this.props.tempSelect;
    const { tempBuildingInfoFormValue } = this.props.tempBuildingInfo;

    /**
     * @description 建立可用付款方式
    */
    const handleCreatePaymentMethod = () => {
      if (Object.keys(this.state.paymentMethod.PAYMENT_INFO.PAYMENT_OPTION[0]).length === 0) return;

      let insuredDate;
      let applyType;
      if (this.props.boPayby.APPLY_NO) {
        insuredDate = this.props.boPayby.START_DATE;
        applyType = this.props.boPayby.BC_TYPE;
      } else {
        if (!tempFillState || !tempBuildingInfoFormValue) return;

        insuredDate = tempBuildingInfoFormValue.INSURED_DATE;
        applyType = tempFillState.applyType;
      }

      const paymentOpt = { ...this.state.paymentMethod.PAYMENT_INFO.PAYMENT_OPTION[0] };
      const optKeys = Object.keys(paymentOpt);
      const showBarcode = applyType === ApplyType.WebRequire && moment(insuredDate).isAfter(moment().add(7, 'd'));

      return _.map(optKeys, (optKey: string, index: number) => {
        if (optKey === PaymentMethod.BarCode && !showBarcode) return;
        if (!this.state.showCreditCardMethod && optKey === PaymentMethod.CreditCard) return;

        return (
          <div className="element__radio-group" key={index}>
            <label htmlFor={`credit-card-radio-${optKey}`}>
              <div className="element__radio">
                <Field
                  type="radio"
                  name="PAYMENT_METHOD"
                  id={`credit-card-radio-${optKey}`}
                  value={optKey}
                  onClick={(e: React.ChangeEvent<HTMLInputElement>) => this.handlePaymentMethodClick(e.currentTarget.value)}
                />
                <div className="element__radio-btn" />
              </div>
              <span>{paymentOpt[optKey]}</span>
            </label>
          </div>
        );
      });
    };

    /**
     * @description 建立 BarCode 區塊
     */
    const handleCreateBarCode = () => {
      let policyholderName;
      let dueDate;
      let insuredName;
      let activePlanName;
      let fee;
      if (this.props.boPayby.APPLY_NO) {
        const policyholders =
          _.filter(this.props.boPayby.PAYER_LIST, (payer: PayerContent) => payer.LTYPE === RuleCheckInsuredType.Applicant);

        const insureds =
          _.filter(this.props.boPayby.PAYER_LIST, (payer: PayerContent) => payer.LTYPE === RuleCheckInsuredType.Insured);

        policyholderName = policyholders[0].NAME;
        dueDate = moment(this.props.boPayby.START_DATE).isValid()
          ? moment(this.props.boPayby.START_DATE).add(-7, 'd').format('YYYY年MM月DD日 12:00')
          : '';
        insuredName = insureds[0].NAME;
        activePlanName = this.props.boPayby.PROD_NAME;
        fee = this.props.boPayby.POLICY_FEE;
      }

      if (tempFillFormValue && tempSelectState) {
        policyholderName = tempFillFormValue.POLICYHOLDERS[0].POLICYHOLDER_NAME;
        dueDate = moment(tempBuildingInfoFormValue?.INSURED_DATE).add(-7, 'd').format('YYYY年MM月DD日 12:00');
        insuredName = tempFillFormValue.INSUREDS[0].INSURED_NAME;
        activePlanName = tempSelectState.activePlanName;
        fee = tempSelectState.evalFireRes.TOTAL_FEE;
      }

      return (
        <div className="insure-process-payment__list">
          <div className="element__list">
            <div className="element__list-col">
              <div className="element__list-group">
                <div className="element__list-group-title">要保人姓名</div>
                <div className="element__list-group-content">{policyholderName}</div>
              </div>
            </div>
            <div className="element__list-col">
              <div className="element__list-group">
                <div className="element__list-group-title">繳費期限</div>
                <div className="element__list-group-content">{dueDate}</div>
              </div>
            </div>
            <div className="element__list-col">
              <div className="element__list-group">
                <div className="element__list-group-title">被保險人姓名</div>
                <div className="element__list-group-content">{insuredName}</div>
              </div>
            </div>
            <div className="element__list-col">
              <div className="element__list-group">
                <div className="element__list-group-title">投保方案</div>
                <div className="element__list-group-content">{activePlanName}</div>
              </div>
            </div>
            <div className="element__list-col">
              <div className="element__list-group">
                <div className="element__list-group-title">保費金額</div>
                <div className="element__list-group-content">{`NT$ ${commonService.thousandFormat(`${fee}`)} 元`}</div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    /**
      * @description 建立付款提示區塊
     */
    const handleCreatePaymentInfo = () => {
      let applyType;
      let applyCantName;
      if (this.props.boPayby.APPLY_NO) {
        applyType = this.props.boPayby.BC_TYPE;

        const policyholders =
          _.filter(this.props.boPayby.PAYER_LIST, (payer: PayerContent) => payer.LTYPE === RuleCheckInsuredType.Applicant);

        applyCantName = policyholders[0].NAME;
      } else {
        if (!this.props.tempFill || !this.props.tempFill.tempFillFormValue || !this.props.tempFill.tempFillState) return;

        const { tempFillFormValue, tempFillState } = this.props.tempFill;

        applyType = tempFillState.applyType;

        applyCantName = tempFillFormValue.POLICYHOLDERS[0].POLICYHOLDER_NAME;
      }

      if (!applyType || !applyCantName) return;

      let payment: PaymentContent = { ...this.state.paymentMethod.PAYMENT_INFO.COMMON_MSG.BANKACCOUNT };

      if (this.state.activePaymentMethod === PaymentMethod.CreditCard) {
        payment = { ...this.state.paymentMethod.PAYMENT_INFO.COMMON_MSG.CREDITCARD };
      }

      let paymentInfoStr: string = payment.NOTICE_B;
      if (applyType === ApplyType.WebInsured) {
        paymentInfoStr = payment.NOTICE_C;
      }

      return (
        <div className="insure-process-payment__part">
          <span className="insure-process-payment__part-title">{`親愛的${applyCantName}您好，`}</span>
          <p>{paymentInfoStr}</p>
          {
            this.state.activePaymentMethod === PaymentMethod.CreditCard
              ? <span className="element__link" onClick={() => handleShowAvailableBank(payment)}>目前可配合之發卡銀行</span>
              : null
          }
        </div>
      );
    };

    /**
      * @description 顯示可用銀行
     */
    const handleShowAvailableBank = (payment: PaymentContent) => {
      const msg = _.reduce(_.map(payment.BANK, (content: BankContent) => content.NAME), (res: string, curr: string) => {
        return `${res}、${curr}`;
      });

      sweetAlertService.modal({
        title: '可配合之發卡銀行',
        html: `<div class="cooperate-bank-modal__content">${msg}</div>`
      });
    };

    /**
      * @description 建立線上繳費權益告知聲明事項
     */
    const handleCreatePaymentClaim = () => {
      return (
        <>
          <span className="insure-process-payment__part-title">{this.state.paymentMethod.PAYMENT_INFO.COMMON_MSG.CLAIMS.HEAD}</span>
          <p>{commonService.htmlParser(this.state.paymentMethod.PAYMENT_INFO.COMMON_MSG.CLAIMS.BODY)}</p>
          <br />
          <div className="element__checkbox-group">
            <label htmlFor="receive-info-checkbox">
              <div className="element__checkbox">
                <Field
                  type="checkbox"
                  name="PAYMENT_CLAIM"
                  id="receive-info-checkbox"
                  onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                    this.setState({ agreeContract: e.currentTarget.checked });
                  }}
                />
                <div className="element__checkbox-btn" />
              </div>
              <span>{this.state.paymentMethod.PAYMENT_INFO.COMMON_MSG.CLAIMS.ANSWER.BODY}</span>
            </label>
          </div>
        </>
      );
    };

    /**
      * @description 建立要被保人選項
     */
    const handleCreateInsRole = () => {
      return _.map(this.state.insRoleTypeSysOpts, (sysOpt: SysOption, index: number) => {
        return (
          <div className="element__radio-group" key={index}>
            <label htmlFor={`guarantor-radio${index}`}>
              <div className="element__radio">
                <Field
                  type="radio"
                  disabled={this.state.isSelf || (this.state.activePaymentMethod === PaymentMethod.CreditCard && (this.state.onlyApplicant || this.state.onlyInsured))}
                  name={this.state.activePaymentMethod === PaymentMethod.CreditCard ? 'CREDIT_PAYER_TYPE' : 'BANK_PAYER_TYPE'}
                  id={`guarantor-radio${index}`}
                  value={sysOpt.PARAM_VALUE}
                  onClick={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const activeInsRoleType = this.state.activeInsRoleType;
                    if (activeInsRoleType !== e.currentTarget.value) {
                      const fieldName = this.state.activePaymentMethod === PaymentMethod.CreditCard ? 'CREDIT_PAYER_ID' : 'BANK_PAYER_ID';
                      this.formikRef.current.setFieldValue(fieldName, '');
                    }
                    this.setState({ activeInsRoleType: e.currentTarget.value });
                  }}
                />
                <div className="element__radio-btn" />
              </div>
              <span className="span-nowrap">{sysOpt.PARAM_TEXT}</span>
            </label>
          </div>
        );
      });
    };

    /**
      * @description 取得要被保人下拉選單內容
     */
    const handleRoleTypeOpts = () => {
      switch (this.state.activeInsRoleType) {
        case InsuredRoleType.Application: {
          if (this.props.boPayby.APPLY_NO) {
            return _.map(this.props.boPayby.PAYER_LIST, (payer: PayerContent, index: number) => {
              if (payer.LTYPE === RuleCheckInsuredType.Insured || (payer.ID_TYPE === NationalType.Legal &&
                this.state.activePaymentMethod === PaymentMethod.CreditCard)) return;

              return <option key={index} value={payer.ID}>{payer.NAME}</option>;
            });
          } else {
            const { tempFillFormValue } = this.props.tempFill;
            if (!tempFillFormValue) return;

            return _.map(tempFillFormValue.POLICYHOLDERS, (holder: BaseFirePolicyHolderContent, index: number) => {
              if (holder.POLICYHOLDER_NATIONALITY_TYPE === NationalType.Legal &&
                this.state.activePaymentMethod === PaymentMethod.CreditCard) return;

              return <option key={index} value={holder.POLICYHOLDER_ID}>{holder.POLICYHOLDER_NAME}</option>;
            });
          }
        }
        case InsuredRoleType.Insured: {
          if (this.props.boPayby.APPLY_NO) {
            return _.map(this.props.boPayby.PAYER_LIST, (payer: PayerContent, index: number) => {
              if (payer.LTYPE === RuleCheckInsuredType.Applicant || (payer.ID_TYPE === NationalType.Legal &&
                this.state.activePaymentMethod === PaymentMethod.CreditCard)) return;

              return <option key={index} value={payer.ID}>{payer.NAME}</option>;
            });
          } else {
            const { tempFillFormValue } = this.props.tempFill;
            if (!tempFillFormValue) return;

            return _.map(tempFillFormValue.INSUREDS, (insured: BaseFireInsuredContent, index: number) => {
              if (insured.INSURED_NATIONALITY_TYPE === NationalType.Legal &&
                this.state.activePaymentMethod === PaymentMethod.CreditCard) return;

              return <option key={index} value={insured.INSURED_ID}>{insured.INSURED_NAME}</option>;
            });
          }
        }
      }
    };

    /**
      * @description 建立銀行下拉選單
     */
    const handleCreateBankOpts = () => {
      const payment: PaymentContent = { ...this.state.paymentMethod.PAYMENT_INFO.COMMON_MSG.BANKACCOUNT };

      return _.map(payment.BANK, (bank: BankContent, index: number) => {
        return <option value={bank.ID} key={index}>{`${bank.ID} ${bank.NAME}`}</option>;
      });
    };

    return (
      <div className="insure-process-payment">
        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          onSubmit={this.handleFormSubmit}
          enableReinitialize
          innerRef={this.formikRef}
        >
          {({ values, errors, touched, handleChange, setFieldTouched }) => (
            <Form>
              <div className="insure-process-payment__title">選擇支付方式</div>
              <div className="insure-process-payment__content">
                <div className="insure-process-payment__row">
                  {
                    handleCreatePaymentMethod()
                  }
                </div>
                {
                  values.PAYMENT_METHOD === PaymentMethod.BarCode && handleCreateBarCode()
                }
                {
                  values.PAYMENT_METHOD !== PaymentMethod.BarCode && handleCreatePaymentInfo()
                }
                {(values.PAYMENT_METHOD !== PaymentMethod.BarCode) && (
                  <div className="insure-process-payment__part">
                    {
                      handleCreatePaymentClaim()
                    }
                  </div>
                )}
                <div className="insure-process-payment__payment-form insure-process-payment__payment-form--N">
                  {/* 信用卡扣款 */}
                  {values.PAYMENT_METHOD === PaymentMethod.CreditCard && this.state.agreeContract && (
                    <div className="insure-process-payment__credit-cards">
                      <div className="insure-process-payment__credit-card">
                        <div className="insure-process-payment__credit-card-container">
                          <div className="insure-process-payment__row">
                            <span>金額</span>
                            <span>{this.handleGetFee(tempSelectState)}</span>
                          </div>
                          <div className="insure-process-payment__row">
                            <div className={'element__form-group' + (
                              (errors.PAN1st && touched.PAN1st) ||
                                (errors.PAN2nd && touched.PAN2nd) ||
                                (errors.PAN3rd && touched.PAN3rd) ||
                                (errors.PAN4th && touched.PAN4th) ? ' element__form-group--invalid' : ''
                            )}
                            >
                              <div className="element__form-group-title">信用卡卡號</div>
                              <div className="element__form-group-content">
                                <Field
                                  component={InputWithRegex}
                                  name="PAN1st"
                                  id="PAN1st"
                                  className="element__input"
                                  maxLength="4"
                                  onBlur={() => {
                                    setFieldTouched('PAN1st', true);
                                  }}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    handleChange(e);
                                    commonService.setInputAutoFocus('PAN1st', 'PAN2nd');
                                  }}
                                />

                                <Field
                                  component={InputWithRegex}
                                  name="PAN2nd"
                                  id="PAN2nd"
                                  className="element__input"
                                  maxLength="4"
                                  onBlur={() => {
                                    setFieldTouched('PAN2nd', true);
                                  }}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    handleChange(e);
                                    commonService.setInputAutoFocus('PAN2nd', 'PAN3rd');
                                  }}
                                />
                                <Field
                                  component={InputWithRegex}
                                  name="PAN3rd"
                                  id="PAN3rd"
                                  className="element__input"
                                  maxLength="4"
                                  onBlur={() => {
                                    setFieldTouched('PAN3rd', true);
                                  }}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    handleChange(e);
                                    commonService.setInputAutoFocus('PAN3rd', 'PAN4th');
                                  }}
                                />
                                <Field
                                  component={InputWithRegex}
                                  name="PAN4th"
                                  id="PAN4th"
                                  className="element__input"
                                  maxLength="4"
                                  onBlur={() => {
                                    setFieldTouched('PAN4th', true);
                                  }}
                                />
                              </div>
                              <div className="element__error-message">
                                {
                                  errors.PAN1st && touched.PAN1st ? <ErrorMessage name="PAN1st" />
                                    : errors.PAN2nd && touched.PAN2nd ? <ErrorMessage name="PAN2nd" />
                                      : errors.PAN3rd && touched.PAN3rd ? <ErrorMessage name="PAN3rd" />
                                        : errors.PAN4th && touched.PAN4th ? <ErrorMessage name="PAN4th" /> : ''
                                }
                              </div>
                            </div>
                          </div>
                          <div className="insure-process-payment__row">
                            <div className={'element__form-group' + (
                              (errors.EXPIREDATE_MONTH && touched.EXPIREDATE_MONTH) ||
                                (errors.EXPIREDATE_YEAR && touched.EXPIREDATE_YEAR) ? ' element__form-group--invalid' : ''
                            )}
                            >
                              <div className="element__form-group-title">信用卡到期(月/年) <span className="ant-picker-exam-info">範例：(12/20)</span></div>
                              <div className="element__form-group-content">
                                <Field
                                  component={InputWithRegex}
                                  id="expire-date-month-input"
                                  name="EXPIREDATE_MONTH"
                                  className="element__input"
                                  maxLength="2"
                                  onBlur={() => {
                                    setFieldTouched('EXPIREDATE_MONTH', true);
                                  }}
                                  validate={(val: any) => {
                                    const reg = /((0[1-9]{1})|(1[0-2]{1}))/;
                                    if (val && !reg.test(val)) return '有效月份需為 01-12';

                                    return '';
                                  }}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    handleChange(e);
                                    commonService.setInputAutoFocus('expire-date-month-input', 'expire-date-year-input');
                                  }}
                                />
                                <span>/</span>
                                <Field
                                  component={InputWithRegex}
                                  id="expire-date-year-input"
                                  name="EXPIREDATE_YEAR"
                                  className="element__input"
                                  maxLength="2"
                                  onBlur={() => {
                                    setFieldTouched('EXPIREDATE_YEAR', true);
                                  }}
                                  validate={(val: any) => {
                                    const nowYear = moment().format('YYYY');
                                    const validYear = +nowYear.substr(2, 2);
                                    if (val && +val < validYear) return `有效年份需大於 (等於) ${nowYear}`;

                                    return '';
                                  }}
                                />
                              </div>
                              <div className="element__error-message">
                                {
                                  errors.EXPIREDATE_MONTH && touched.EXPIREDATE_MONTH
                                    ? <ErrorMessage name="EXPIREDATE_MONTH" />
                                    : errors.EXPIREDATE_YEAR && touched.EXPIREDATE_YEAR
                                      ? <ErrorMessage name="EXPIREDATE_YEAR" /> : ''
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="insure-process-payment__credit-card">
                        <div className="insure-process-payment__credit-card-remind">{commonService.getCodeMsg(this.props.UICodes, CodeId.U032)}</div>
                        <div className="insure-process-payment__credit-card-container">
                          <div className="insure-process-payment__row">
                            <div className="element__form-group">
                              <div className="element__form-group-content">
                                {
                                  this.state.insRoleTypeSysOpts.length > 0 && handleCreateInsRole()
                                }
                              </div>
                              <div className="element__error-message">
                                <ErrorMessage name="CREDIT_PAYER_TYPE" />
                              </div>
                            </div>
                          </div>
                          <div className="insure-process-payment__row">
                            <div className={'element__form-group' + ((errors.CREDIT_PAYER_ID && touched.CREDIT_PAYER_ID) ? ' element__form-group--invalid' : '')}>
                              <div className="element__form-group-title">扣款人姓名</div>
                              <Field as="select" disabled={this.state.isSelf} name="CREDIT_PAYER_ID" className="element__select">
                                <option value="">請選擇</option>
                                {
                                  handleRoleTypeOpts()
                                }
                              </Field>
                              <div className="element__error-message">
                                <ErrorMessage name="CREDIT_PAYER_ID" />
                              </div>
                            </div>
                            <div className={'element__form-group' + ((errors.CARD_VERIFY_CODE && touched.CARD_VERIFY_CODE) ? ' element__form-group--invalid' : '')}>
                              <div className="element__form-group-title">三碼檢查碼</div>
                              <Field
                                component={InputWithRegex}
                                id="card-verify-code-input"
                                autoComplete="off"
                                name="CARD_VERIFY_CODE"
                                className="element__input" maxLength={3}
                                onBlur={() => {
                                  setFieldTouched('CARD_VERIFY_CODE', true);
                                }}
                              />
                              <div className="element__error-message">
                                <ErrorMessage name="CARD_VERIFY_CODE" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* 活期帳戶扣款 */}
                  {values.PAYMENT_METHOD === PaymentMethod.BankAccount && this.state.agreeContract && (
                    <div className="insure-process-payment__current-account">
                      <div className="insure-process-payment__current-account-container">
                        <div className="insure-process-payment__row">
                          <span>金額</span>
                          <span>{this.handleGetFee(tempSelectState)}</span>
                        </div>
                        <div className="insure-process-payment__row">
                          <div className={'element__form-group' + ((errors.BANK_CODE && touched.BANK_CODE) ? ' element__form-group--invalid' : '')}>
                            <div className="element__form-group-content">
                              <span>銀行</span>
                              <Field as="select" name="BANK_CODE" className="element__select">
                                <option value="">請選擇</option>
                                {
                                  handleCreateBankOpts()
                                }
                              </Field>
                            </div>
                            <div className="element__error-message">
                              <ErrorMessage name="BANK_CODE" className="element__error-message" />
                            </div>
                          </div>
                        </div>
                        <div className="insure-process-payment__row">
                          <div className={'element__form-group' + ((errors.BANK_ACCOUNT && touched.BANK_ACCOUNT) ? ' element__form-group--invalid' : '')}>
                            <div className="element__form-group-content">
                              <span>帳號</span>
                              <Field
                                component={InputWithRegex}
                                id="bank-account-input"
                                autoComplete="off"
                                name="BANK_ACCOUNT"
                                className="element__input"
                                maxLength="14"
                                onBlur={() => {
                                  setFieldTouched('BANK_ACCOUNT', true);
                                }}
                              />
                            </div>
                            <div className="element__error-message">
                              <ErrorMessage name="BANK_ACCOUNT" />
                            </div>
                          </div>
                        </div>
                        <div className="insure-process-payment__row">
                          <div className="element__form-group">
                            <div className="element__form-group-content">
                              {
                                this.state.insRoleTypeSysOpts.length > 0 && handleCreateInsRole()
                              }
                            </div>
                            <div className="element__error-message">
                              <ErrorMessage name="BANK_PAYER_TYPE" />
                            </div>
                          </div>
                        </div>
                        <div className="insure-process-payment__row">
                          <div className="element__form-group">
                            <Field as="select" disabled={this.state.isSelf} name="BANK_PAYER_ID" className="element__select">
                              <option value="">請選擇</option>
                              {
                                handleRoleTypeOpts()
                              }
                            </Field>
                            <div className="element__error-message">
                              <ErrorMessage name="BANK_PAYER_ID" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* 行動條碼繳費 */}
                  {values.PAYMENT_METHOD === PaymentMethod.BarCode && (
                    <>
                      <div className="insure-process-payment__part">
                        <span className="insure-process-payment__part-title">行動條碼支付方式及說明：</span>
                        {
                          _.map(this.state.barcodeNoticeCodes, (code: Code, index: number) => {
                            return <p className="barcode-info" key={index}>{commonService.htmlParser(code.CODE_VALUE)}</p>;
                          })
                        }
                      </div>
                      {/**
                        <div className="insure-process-payment__mobile-barcode">
                          <div className="insure-process-payment__mobile-barcode-container">
                            <div className="insure-process-payment__mobile-barcode-title">條碼資訊</div>
                            <div className="insure-process-payment__mobile-barcode-img">
                              <img src={require('assets/images/mobile-barcode-img-1.png')} alt="mobile-barcode-img-1" />
                            </div>
                            <div className="insure-process-payment__mobile-barcode-img">
                              <img src={require('assets/images/mobile-barcode-img-2.png')} alt="mobile-barcode-img-2" />
                            </div>
                            <div className="insure-process-payment__mobile-barcode-img">
                              <img src={require('assets/images/mobile-barcode-img-3.png')} alt="mobile-barcode-img-3" />
                            </div>
                          </div>
                        </div>
                    */}
                      <div className="insure-process-payment__part">
                        <span className="insure-process-payment__part-title">注意事項</span>
                        <p>
                          {
                            _.map(this.state.barcodeNoteCodes, (code: Code, index: number) => {
                              return React.cloneElement(
                                <>
                                  {`${code.CODE_VALUE}`}<br />
                                </>
                                , { key: index }
                              );
                            })
                          }
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="insure-process-payment__controls">
                <button
                  type="button" className="element__button element__button--red-border" onClick={() => {
                    if (!this.props.boPayby.APPLY_NO) {
                      this.props.onGoBack();
                    }
                  }}
                >返回上一頁
                </button>
                <button id="btn_pay" type="submit" className="element__button element__button--red" disabled={!this.state.agreeContract && values.PAYMENT_METHOD !== PaymentMethod.BarCode}>確認支付</button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default InsureProcessPayment;
